import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Mail, Copy, Trash2, Check, UserPlus, UserMinus } from "lucide-react";
import ManagerTimeOffQueue from "@/components/ManagerTimeOffQueue";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function ManagerSonographers() {
  const [list, setList] = useState([]);
  const [invites, setInvites] = useState([]);
  const [form, setForm] = useState({ email: "", name: "" });
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState("");
  const [removingId, setRemovingId] = useState("");
  const [removeErr, setRemoveErr] = useState("");

  const load = async () => {
    const [s, i] = await Promise.all([
      api.get("/clinics/mine/sonographers"),
      api.get("/clinics/mine/invitations"),
    ]);
    setList(s.data); setInvites(i.data);
  };
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/clinics/mine/invitations", form);
      setForm({ email: "", name: "" });
      load();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
  };

  const revoke = async (t) => { await api.delete(`/clinics/mine/invitations/${t}`); load(); };

  const disconnect = async (uid) => {
    setRemovingId(uid); setRemoveErr("");
    try {
      await api.delete(`/clinics/mine/sonographers/${uid}`);
      await load();
    } catch (e) {
      setRemoveErr(formatApiError(e.response?.data?.detail) || "Could not disconnect sonographer");
    } finally {
      setRemovingId("");
    }
  };

  const copy = (token) => {
    const url = `${window.location.origin}/register?invite=${token}`;
    navigator.clipboard.writeText(url);
    setCopied(token);
    setTimeout(() => setCopied(""), 1500);
  };

  const pending = invites.filter((i) => (i.status ? i.status === "pending" : !i.used));
  const history = invites.filter((i) => (i.status ? i.status !== "pending" : i.used));

  return (
    <div>
      <span className="pill">Sonographer Management</span>
      <h1 className="mt-2 text-3xl font-bold">Manage your team.</h1>
      <p className="text-zinc-500 mt-1">Invite sonographers by email. Only people who accept your invitation are linked to your clinic.</p>

      <form onSubmit={invite} className="card-elev mt-8" data-testid="invite-form">
        <h3 className="font-bold mb-3 flex items-center gap-2"><UserPlus className="w-4 h-4" style={{ color: "var(--gold)" }} />Invite a sonographer</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 px-4 rounded-md border border-zinc-200" data-testid="invite-email" />
          <input placeholder="Name (optional)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 px-4 rounded-md border border-zinc-200" data-testid="invite-name" />
          <button className="btn-gold" data-testid="invite-submit"><Mail className="w-4 h-4 mr-2" />Generate invitation</button>
        </div>
        {err && <div className="text-sm text-red-600 mt-2">{err}</div>}
      </form>

      <div className="card-elev mt-6">
        <h3 className="font-bold mb-3">Pending invitations</h3>
        {pending.length === 0 ? (
          <p className="text-sm text-zinc-500">No pending invitations.</p>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {pending.map((i) => {
              const url = `${window.location.origin}/register?invite=${i.token}`;
              return (
                <li key={i.token} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`invite-row-${i.token}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold">{i.email}</div>
                    <div className="text-xs text-zinc-500 mt-0.5 truncate">{url}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => copy(i.token)} className="btn-outline h-10 px-4 text-sm" data-testid={`invite-copy-${i.token}`}>
                      {copied === i.token ? <><Check className="w-4 h-4 mr-1" />Copied</> : <><Copy className="w-4 h-4 mr-1" />Copy link</>}
                    </button>
                    <button onClick={() => revoke(i.token)} className="btn-ghost text-red-600" data-testid={`invite-revoke-${i.token}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="card-elev mt-6">
        <h3 className="font-bold mb-3">Invitation history</h3>
        {history.length === 0 ? (
          <p className="text-sm text-zinc-500">No previous invitations.</p>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {history.map((i) => (
              <li key={i.token} className="py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`invite-hist-${i.token}`}>
                <span className="font-medium">{i.email}</span>
                <span className={`text-xs px-2 py-1 rounded-full font-semibold uppercase tracking-[0.12em] ${i.status === "accepted" ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>{i.status || (i.used ? "accepted" : "pending")}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card-elev mt-6">
        <h3 className="font-bold mb-3">Your sonographers ({list.length})</h3>
        {removeErr && <div className="text-sm text-red-600 mb-2" data-testid="disconnect-error">{removeErr}</div>}
        {list.length === 0 ? (
          <p className="text-sm text-zinc-500">No sonographers linked yet. Send an invite above.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-[0.15em] text-zinc-500">
              <tr><th className="py-2">Name</th><th>Email</th><th>Joined</th><th className="text-right">Actions</th></tr>
            </thead>
            <tbody>
              {list.map((u) => (
                <tr key={u.user_id} className="border-t border-zinc-100" data-testid={`sono-row-${u.user_id}`}>
                  <td className="py-2 font-semibold">{u.name}</td>
                  <td>{u.email}</td>
                  <td className="text-zinc-500">{u.created_at?.slice(0, 10)}</td>
                  <td className="text-right">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          className="btn-ghost text-red-600 text-xs h-9 px-3"
                          disabled={removingId === u.user_id}
                          data-testid={`sono-disconnect-${u.user_id}`}
                        >
                          <UserMinus className="w-4 h-4 mr-1" />
                          {removingId === u.user_id ? "Removing…" : "Disconnect"}
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Disconnect {u.name || u.email}?</AlertDialogTitle>
                          <AlertDialogDescription asChild>
                            <div className="space-y-3 text-sm">
                              <p>They will be removed from your clinic roster, analytics and hotspots.</p>
                              <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
                                <div className="font-semibold text-zinc-900 mb-1">Their personal data stays with them.</div>
                                <ul className="list-disc pl-5 text-zinc-600 space-y-0.5">
                                  <li>Account, email and password remain active.</li>
                                  <li>Every pain log they recorded is preserved.</li>
                                  <li>Their personal dashboard and statistics keep working on next login.</li>
                                </ul>
                              </div>
                              <p className="text-zinc-500">They can re-join this clinic (or another) anytime by accepting a new invitation.</p>
                            </div>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid={`sono-disconnect-cancel-${u.user_id}`}>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => disconnect(u.user_id)}
                            data-testid={`sono-disconnect-confirm-${u.user_id}`}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Disconnect
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ManagerTimeOffQueue />
    </div>
  );
}
